import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, InputBaseProps, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { Add, ArrowBack } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { TextField } from '../../../../../../ui-kit';
import { useDebounce } from '../../../../../../utils';
import { DeliveryListSuppliersQuery, useDeliveryListSuppliersQuery } from '../../../../../../generated/graphql';

const LIMIT = 10;

interface MobileSupplierSearchProps {
    onClose(): void;
    onCreateNewClick(): void;
    passedSearchValue?: string;
    onSearchResultClick(supplier: DeliveryListSuppliersQuery['suppliers'][0]): void;
}

const MobileSupplierSearch = ({ onClose, onCreateNewClick, passedSearchValue, onSearchResultClick }: MobileSupplierSearchProps) => {
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const { data, fetchMore, loading, refetch } = useDeliveryListSuppliersQuery({ nextFetchPolicy: 'cache-first' });

    const { t } = useTranslation();

    const debouncedSearchQuery = useDebounce(searchQuery);
    let suppliers: DeliveryListSuppliersQuery['suppliers'] = data ? data.suppliers : [];

    useEffect(() => {
        if(suppliers.length === 0 || suppliers.length % LIMIT !== 0){
            setHasMore(false);
        } else {
            setHasMore(true);
        }
    }, [suppliers]);

    useEffect(() => {
        if(passedSearchValue){
            setSearchQuery(passedSearchValue);
        }
    }, [passedSearchValue]);

    useEffect(() => {
        setOffset(0);
        refetch({ multiValueSearch: debouncedSearchQuery, offset: 0, limit: LIMIT });
    }, [debouncedSearchQuery, refetch]);

    const handleFetchMore = async () => {
        if(fetchMore){
            const newOffset = offset + LIMIT;
            setIsFetchingMore(true);
            const { data: newData } = await fetchMore({ variables: { offset: newOffset } });
            setIsFetchingMore(false);
            setOffset(newOffset);
            ({ suppliers } = newData);
            if(suppliers.length < LIMIT){
                setHasMore(false);
            }
        }

    };
    if(data){
        ({ suppliers } = data);
    }

    const handleInputChange: InputBaseProps['onChange'] = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setSearchQuery(value);
    };

    const shouldShowLoader = loading && suppliers.length === 0;

    return (
        <Box px={3} py={2}>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <ArrowBack />
            </IconButton>
            <Box mb={2} />
            <TextField
                name='search'
                label={t(`Type a name or SKU`)}
                fullWidth
                value={searchQuery}
                onChange={handleInputChange}
            />
            <List>
                <ListItem
                    button
                    onClick={onCreateNewClick}
                >
                    <ListItemIcon>
                        <Add color='primary' />
                    </ListItemIcon>
                    <ListItemText primary={t('New supplier')} />
                </ListItem>
                {suppliers.map(supplier => (
                    <ListItem
                        key={supplier.id}
                        button
                        onClick={() => onSearchResultClick(supplier)}
                    >
                        <ListItemText primary={supplier.legalName} />
                    </ListItem>
                ))}
                {shouldShowLoader
                    ? (
                        <ListItem>
                            <Box justifyContent='center' display='flex' flex={1}>
                                <CircularProgress />
                            </Box>
                        </ListItem>
                    ) : null}
            </List>
            {hasMore ? (
                <Button
                    endIcon={isFetchingMore ? <CircularProgress size={16} /> : null}
                    disabled={isFetchingMore}
                    fullWidth
                    onClick={handleFetchMore}
                >
                    {t('Show more')}
                </Button>
                ) : null}
        </Box>
    );
};

export default MobileSupplierSearch;
